import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import MuiInputLabel from "@mui/material/InputLabel";
import { Slot } from "@radix-ui/react-slot";
import React from "react";
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
);

export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ ...props }: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
}

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

export function FormItem({
  children,
  xs,
}: {
  children: React.ReactNode;
  xs: number;
}) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <Grid item xs={xs}>
        {children}
      </Grid>
    </FormItemContext.Provider>
  );
}
FormItem.displayName = "FormItem";

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

export function FormInputLabel({ children }: { children: React.ReactNode }) {
  const { id } = React.useContext(FormItemContext);

  return (
    <MuiInputLabel htmlFor={id} id={`${id}-label`}>
      {children}
    </MuiInputLabel>
  );
}

export const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = "FormControl";

export function FormMessage() {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : null;

  if (!body) {
    return null;
  }

  return (
    <FormHelperText error id={formMessageId}>
      {body}
    </FormHelperText>
  );
}
FormMessage.displayName = "FormMessage";
